import React, { useEffect, useState } from 'react';
import { Button, Modal } from '@myua/kit';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import * as actions from '../actions';
import {
  JobStatus,
  getJobUploadProgress,
  jobIsFailed,
  jobIsSettled,
  jobIsSuccessful,
} from '../model';
import { getJobs, getModalOpen, getPendingUpload } from '../selectors';
import { useUploader } from '../useUploader';
import { useUploadTarget } from '../useUploadTarget';
import { UploadDestination, UploadDropzone } from './UploadTargetFields';

const formatDuration = (ms: number): string => {
  const d = moment.duration(ms);
  const parts: string[] = [];
  if (d.days() > 0) parts.push(`${d.days()} day${d.days() !== 1 ? 's' : ''}`);
  if (d.hours() > 0) parts.push(`${d.hours()} hour${d.hours() !== 1 ? 's' : ''}`);
  if (d.minutes() > 0) parts.push(`${d.minutes()} min`);
  if (d.seconds() > 0 && parts.length < 2) parts.push(`${d.seconds()} sec`);
  return parts.length > 0 ? parts.join(' ') : '<1 sec';
};

const formatSpeed = (bytesPerSec: number): string => {
  if (bytesPerSec === 0) return '0 B/s';
  const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  let value = bytesPerSec;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
};

export const UploadModal: React.FC = () => {
  const dispatch = useDispatch();
  const { cancelAll, retryJob } = useUploader();
  const jobs = useSelector(getJobs);
  const open = useSelector(getModalOpen);
  const pendingUpload = useSelector(getPendingUpload);

  // The destination (existing folder or a new one) is picked right in the modal.
  const target = useUploadTarget(open);

  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [dropzoneRevealed, setDropzoneRevealed] = useState(false);

  const hasJobs = jobs.length > 0;
  const allSettled = hasJobs && jobs.every(jobIsSettled);
  const isUploading = hasJobs && !allSettled;
  const showDropzone = (!hasJobs && !pendingUpload) || dropzoneRevealed;
  const showStatus = hasJobs && !dropzoneRevealed;
  const showPendingStatus = pendingUpload && !hasJobs && !dropzoneRevealed;

  const totalSize = jobs.reduce((s, j) => s + j.fileSize, 0);
  const uploadedSize = jobs.reduce((s, j) => s + j.fileSize * getJobUploadProgress(j), 0);
  const overallProgress = totalSize > 0 ? uploadedSize / totalSize : 0;

  const runningJobs = jobs.filter((j) => j.status === JobStatus.UPLOAD_INPROGRESS);
  const overallSpeed = runningJobs.reduce((s, j) => s + (j.uploadSpeed ?? 0), 0);
  const remainingBytes = Math.max(0, totalSize - uploadedSize);
  const overallEta = overallSpeed > 0 ? (remainingBytes / overallSpeed) * 1000 : null;

  const successfulCount = jobs.filter(jobIsSuccessful).length;
  const failedJobs = jobs.filter(jobIsFailed);
  const failedCount = failedJobs.length;
  const totalCount = jobs.length;
  const finishedCount = successfulCount + failedCount;

  // Reveal the error list as soon as something fails: the per-job message is the
  // only place an actionable backend rejection (font license, validation) is shown,
  // and behind a collapsed toggle it read as a bare "Completed with 1 error".
  // Still collapsible afterwards — this only sets the initial state per failure.
  useEffect(() => {
    if (failedCount > 0) setErrorsExpanded(true);
  }, [failedCount]);

  const onFiles = (files: File[]) => {
    // A new folder is created first: switch to "Preparing upload…" right away.
    if (target.newFolderActive) setDropzoneRevealed(false);
    target.startUpload(files);
  };

  const handleClose = () => {
    if (allSettled) {
      dispatch(actions.cancelAllJobs());
    }
    setErrorsExpanded(false);
    dispatch(actions.modalClosed());
  };

  const handleCancelUploads = () => {
    cancelAll();
    setErrorsExpanded(false);
  };

  // auto-expand error log on first error
  const hasErrors = failedCount > 0;
  useEffect(() => {
    if (hasErrors) setErrorsExpanded(true);
  }, [hasErrors]);

  // hide dropzone whenever a new batch starts uploading
  useEffect(() => {
    if (isUploading) setDropzoneRevealed(false);
  }, [isUploading]);

  // reset reveal state when modal closes
  useEffect(() => {
    if (!open) setDropzoneRevealed(false);
  }, [open]);

  return (
    <Modal
      title="Upload files"
      centered
      open={open}
      onCancel={handleClose}
      footer={null}
      width={680}
      className="al-upload-modal"
    >
      <UploadDestination
        target={target}
        disabled={isUploading || Boolean(pendingUpload)}
        autoFocus
      />

      {showDropzone && <UploadDropzone target={target} disabled={!open} onFiles={onFiles} />}

      {showStatus && (
        <div className="al-upload-modal__status">
          <div className="al-upload-modal__progress-bar">
            <div
              className="al-upload-modal__progress-fill"
              style={{ width: `${Math.round(overallProgress * 100)}%` }}
            />
          </div>

          <div className="al-upload-modal__stats">
            <span className="al-upload-modal__stat">
              {Math.round(overallProgress * 100)}% · {finishedCount} of {totalCount} files
            </span>
            {overallSpeed > 0 && (
              <>
                <span className="al-upload-modal__stat-dot">•</span>
                <span className="al-upload-modal__stat">{formatSpeed(overallSpeed)}</span>
              </>
            )}
            {overallEta !== null && overallEta > 0 && Number.isFinite(overallEta) && (
              <>
                <span className="al-upload-modal__stat-dot">•</span>
                <span className="al-upload-modal__stat">{formatDuration(overallEta)} left</span>
              </>
            )}
            {allSettled && (
              <span
                className={
                  failedCount > 0 ? 'al-upload-modal__stat-failed' : 'al-upload-modal__stat-success'
                }
              >
                {failedCount > 0
                  ? `Completed with ${failedCount} error${failedCount === 1 ? '' : 's'}`
                  : 'Upload complete'}
              </span>
            )}
          </div>

          {failedCount > 0 && (
            <div className="al-upload-modal__errors">
              <button
                type="button"
                className="al-upload-modal__errors-toggle"
                onClick={() => setErrorsExpanded(!errorsExpanded)}
              >
                <span
                  className={classNames('al-upload-modal__errors-chevron', {
                    'al-upload-modal__errors-chevron_expanded': errorsExpanded,
                  })}
                >
                  ▶
                </span>
                Errors ({failedCount})
              </button>
              {errorsExpanded && (
                <ul className="al-upload-modal__errors-list">
                  {failedJobs.map((job) => (
                    <li key={job.uuid} className="al-upload-modal__error-item">
                      <span className="al-upload-modal__error-filename" title={job.fileName}>
                        {job.fileName}
                      </span>
                      <span className="al-upload-modal__error-message">
                        {job.errorMessage ??
                          (job.status === JobStatus.INVALID_FILE_NAME
                            ? 'Invalid file name'
                            : 'Upload failed')}
                      </span>
                      {job.status !== JobStatus.INVALID_FILE_NAME && (
                        <button
                          type="button"
                          className="al-upload-modal__error-retry"
                          onClick={() => void retryJob(job.uuid)}
                        >
                          Retry
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {isUploading && (
            <div className="al-upload-modal__actions">
              <Button onClick={handleCancelUploads}>Cancel uploads</Button>
            </div>
          )}

          {allSettled && (
            <div className="al-upload-modal__upload-more-row">
              <button
                type="button"
                className="al-upload-modal__upload-more"
                onClick={() => setDropzoneRevealed(true)}
              >
                Upload more files
              </button>
            </div>
          )}
        </div>
      )}

      {showPendingStatus && (
        <div className="al-upload-modal__status">
          <div className="al-upload-modal__progress-bar al-upload-modal__progress-bar_indeterminate">
            <div className="al-upload-modal__progress-fill al-upload-modal__progress-fill_indeterminate" />
          </div>
          <div className="al-upload-modal__stats">
            <span className="al-upload-modal__stat">Preparing upload…</span>
          </div>
        </div>
      )}

      {(isUploading || pendingUpload) && (
        <div className="al-upload-modal__continue-row">
          <button type="button" className="al-upload-modal__continue" onClick={handleClose}>
            Continue to use Asset Lab
          </button>
        </div>
      )}
    </Modal>
  );
};
